import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { brandColors } from '@config/brandTokens';
import { FAMILY_CHILD_ACTION_BUTTON_SX } from '@components/profile/ResendChildConsentButton';

export const MIN_CHILD_PLAYER_ID_PASSWORD_LENGTH = 8;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export type SetChildPasswordControlProps = {
  playerId: string;
  playerIdCode?: string | null;
  childName?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export async function setChildPlayerIdPassword(playerId: string, password: string) {
  if (password.length < MIN_CHILD_PLAYER_ID_PASSWORD_LENGTH) {
    throw new Error(
      `Child Player ID password must be at least ${MIN_CHILD_PLAYER_ID_PASSWORD_LENGTH} characters`,
    );
  }
  const res = await fetch(`/api/children/${encodeURIComponent(playerId)}/password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  });
  const text = await res.text();
  let json: { success?: boolean; message?: string } = {};
  if (text.trim()) {
    try {
      json = JSON.parse(text);
    } catch {
      json = {};
    }
  }
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Could not update password');
  }
  return json as { success: true; message: string };
}

const SetChildPasswordControl: React.FC<SetChildPasswordControlProps> = ({
  playerId,
  playerIdCode,
  childName,
  disabled,
  disabledReason,
}) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (disabled) return;
    setPassword('');
    setError('');
    setOpen(true);
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
    setPassword('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const json = await setChildPlayerIdPassword(playerId, password);
      toast.success(json.message);
      setOpen(false);
      setPassword('');
    } catch (err: any) {
      setError(err.message || 'Could not update password');
    } finally {
      setSaving(false);
    }
  };

  const button = (
    <Button
      size="small"
      variant="contained"
      disabled={disabled}
      onClick={openDialog}
      sx={FAMILY_CHILD_ACTION_BUTTON_SX}
    >
      Set Password
    </Button>
  );

  const who = childName || 'this child';
  const idLabel = playerIdCode ? ` (${playerIdCode})` : '';

  return (
    <>
      {disabled && disabledReason ? (
        <Tooltip title={disabledReason}>
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Dialog
        open={open}
        onClose={close}
        maxWidth="xs"
        fullWidth
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitle sx={{ color: brandColors.navy, fontWeight: 800 }}>
          Set Player ID password
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: brandColors.slate, mb: 2 }}>
            This is the password for {who}&apos;s Player ID{idLabel} sign-in. Until you set one,
            they can keep using the family password. Your email password does not change.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            size="small"
            type="password"
            label="New Player ID password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            helperText={`At least ${MIN_CHILD_PLAYER_ID_PASSWORD_LENGTH} characters. This password is not emailed.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || password.length < MIN_CHILD_PLAYER_ID_PASSWORD_LENGTH}
            onClick={save}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy }}
          >
            {saving ? 'Saving...' : 'Save password'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetChildPasswordControl;
